import { CreateProjectParams } from '../../api';
import { AnalysisMode, ScriptSkill } from '../../types';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type SaveSource = 'project' | 'style' | 'script';

interface ValidationParams {
  source: SaveSource;
  skillId: string;
  enableScriptNormalization: boolean;
  scriptSkillId: string;
  scriptSkills: ScriptSkill[];
  scriptSkillValues: Record<string, string>;
  t: Translate;
}

export const validateProjectSave = ({
  source,
  skillId,
  enableScriptNormalization,
  scriptSkillId,
  scriptSkills,
  scriptSkillValues,
  t,
}: ValidationParams): string | null => {
  if (!skillId) {
    return t('editModal.selectShotSkillAlert');
  }
  if (source !== 'script') {
    return null;
  }
  if (enableScriptNormalization && !scriptSkillId) {
    return t('editModal.selectScriptSkillAlert');
  }
  if (enableScriptNormalization && scriptSkillId) {
    const skill = scriptSkills.find(item => item.id === scriptSkillId);
    const missing = (skill?.variables || []).filter(variable => variable.required && !scriptSkillValues[variable.key]);
    if (missing.length > 0) {
      return t('editModal.missingScriptParams', { params: missing.map(variable => variable.label).join(', ') });
    }
  }
  return null;
};

interface ProjectPayloadBase {
  name: string;
  projectPrompt: string;
  aspectRatio: string;
  skillId?: string;
  skillVariableValues: Record<string, string>;
  // 台词语种字段必须以原始值（含空串）进 payload：后端 UpdateProject 是 *string
  // 部分更新，字段缺失 = 不修改，空串才是"清除已选语种技能"。
  outputLanguage: string;
  languageSkillId: string;
  languageSkillValues: Record<string, string>;
  autoGenerateShots: boolean;
  analysisMode: AnalysisMode;
  autoStartAnalysis: boolean;
}

interface ScriptPayloadFields {
  scriptSkillId?: string;
  scriptSkillValues?: Record<string, string>;
  enableScriptNormalization: boolean;
}

export const buildProjectUpdatePayload = ({
  source,
  base,
  script,
}: {
  source: SaveSource;
  base: ProjectPayloadBase;
  script: ScriptPayloadFields;
}): CreateProjectParams => {
  const payload: CreateProjectParams = {
    ...base,
    languageSkillValues: base.languageSkillId ? base.languageSkillValues : undefined,
  };
  if (source === 'script') {
    payload.scriptSkillId = script.scriptSkillId || undefined;
    payload.scriptSkillValues = script.enableScriptNormalization ? script.scriptSkillValues : undefined;
    payload.enableScriptNormalization = script.enableScriptNormalization;
  }
  return payload;
};
